"use client";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  Slider,
  Stack,
  IconButton,
  Typography,
  ListItemText
} from "@mui/material";
import {
  LockOutlined as LockedIcon,
  HourglassFullRounded as DownloadingIcon,
  SkipPreviousRounded as PreviousIcon,
  PlayArrowRounded as PlayIcon,
  PauseRounded as PauseIcon,
  SkipNextRounded as ForwardIcon,
  SentimentVeryDissatisfiedRounded as ErrorIcon
} from "@mui/icons-material";

import { observer, useStore } from "../../../../../service/mobx";
import Tooltip from "../../../../../component/Tooltip";
import { fetchFromApiServerRaw } from "../../../../../service/graph";
import { TTSIcon } from "../../../../../component/Icon/TTS";
import useDialogSignIn from "../../../../../component/Dialog/dialogs/appWide/Login";
import useDialogUpgrade from "../../../../../component/Dialog/dialogs/appWide/Upgrade";

function TextToSpeech() {
  const audioRef = useRef(document.getElementById("tts"));
  const { analytics, reader, utilities } = useStore();
  const [action, setAction] = useState(
    0 < reader.tts.seconds
      ? audioRef.current.paused
        ? "Pause"
        : "Play"
      : "idle"
  );
  const locked = useFeatureGate(action === "Play");

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.ontimeupdate = () => {
        // // console.log(reader.tts);
        // Function to update the slider based on the current time
        let previousTrackTime = 0;
        let i = reader.tts.trackIndex;

        while (i--) {
          previousTrackTime += reader.tts.trackDuration[i];
        }
        // console.log(audioRef.current.paused, previousTrackTime);
        // do not update slider state if it's paused, e.g. pausing when idle will cause react state to update before the event listener, bc of event loop order
        if (audioRef.current.paused === false) {
          reader.set.tts({
            ...reader.tts,
            seconds: previousTrackTime + audioRef.current.currentTime
          });
        }
      };
    }
  }, [reader]);

  useEffect(() => {
    if (
      reader.article &&
      audioRef.current &&
      audioRef.current.dataset.paperID !== reader.paperID
    ) {
      const sections = [];

      for (const { heading } of reader.article.sections) {
        if (heading.toLowerCase().includes("references")) {
          break;
        } else {
          sections.push(heading);
        }
      }

      audioRef.current.dataset.paperID = reader.paperID;
      reader.set.tts({
        sections,
        seconds: 0,
        duration: 0,
        trackIndex: 0,
        sectionIndex: 0,
        tracks: [],
        trackDuration: [],
        maxIndex: sections.length - 1
      });
    }
  }, [reader, reader.article]);

  useEffect(() => {
    if (
      audioRef.current &&
      reader.tts.tracks !== undefined &&
      audioRef.current.src !== reader.tts.tracks[reader.tts.trackIndex]
    ) {
      audioRef.current.src = reader.tts.tracks[reader.tts.trackIndex];
    }
  }, [reader, reader.tts.tracks, reader.tts.trackIndex]);

  useEffect(() => {
    if (action === "Play") {
      audioRef.current.play()?.catch(() => {});
    }
  }, [action]);

  const onClick = useCallback(
    (_, newAction, currentSectionIndex = reader.tts.sectionIndex) => {
      const startingIndex = currentSectionIndex;

      newAction ??=
        action === "idle" ? "Download" : action === "Play" ? "Pause" : "Play";

      setAction(newAction);

      // console.log({ newAction });

      if (newAction === "Pause") {
        audioRef.current.pause();
      } else if (newAction === "Play") {
        // audioRef.current.play()?.catch(() => {});
      } else {
        const path = `papers/tts/${reader.publisher}/${reader.paperID}/${currentSectionIndex}`;

        utilities.cache.get(path).then(async base64AudioFiles => {
          try {
            if (!base64AudioFiles) {
              throw "no cache";
            }
          } catch {
            const response = await fetchFromApiServerRaw({
              path
            }).catch(() => {});

            if (response?.ok) {
              analytics.track.usage.for("audio");
              base64AudioFiles = await response.json();
              utilities.cache.set(path, base64AudioFiles);
            }
          }

          // only update track state and start playing the track
          // if the user is still on the same sectionIndex that they were on when
          // they initiated the action to request the audio
          if (startingIndex !== currentSectionIndex) {
            return;
          } else if (!base64AudioFiles) {
            // Set error state
            return setAction("error");
          }

          const tracks = [];

          for (let i = 0, j = base64AudioFiles.length; i < j; i++) {
            const byteCharacters = atob(base64AudioFiles[i]);
            const bytes = [];

            for (let k = 0, l = byteCharacters.length; k < l; k++) {
              bytes[k] = byteCharacters.charCodeAt(k);
            }
            // Now you can create the Blob from the Uint8Array
            const url = URL.createObjectURL(
              new Blob([new Uint8Array(bytes)], { type: "audio/mp3" })
            );
            const track = new Audio(url);

            tracks.push(url);
            track.onloadeddata = () => {
              const trackDuration = [...reader.tts.trackDuration];

              trackDuration[i] = track.duration;
              reader.set.tts({
                ...reader.tts,
                trackDuration,
                duration: reader.tts.duration + track.duration
              });
            };
          }

          reader.tts.tracks.forEach(url => URL.revokeObjectURL(url));
          reader.set.tts({ ...reader.tts, tracks });

          audioRef.current.src = reader.tts.tracks[0];
          audioRef.current.play()?.catch(() => {});
          setAction("Play");
        });
      }

      if (newAction !== "idle") {
        analytics.track.event(`TTS ${newAction}`, {
          index: currentSectionIndex,
          section: reader.tts.sections[currentSectionIndex]
        });
      }
    },
    [analytics, reader, utilities, action]
  );

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.onended = () => {
        const lastTrack = reader.tts.trackDuration.length - 1;
        const nextTrack = Math.min(reader.tts.trackIndex + 1, lastTrack);

        if (nextTrack !== lastTrack) {
          // play next track
          reader.set.tts({ ...reader.tts, trackIndex: nextTrack });
          setAction("Play");
        } else if (reader.tts.sectionIndex !== reader.tts.maxIndex) {
          const newSection = reader.tts.sectionIndex + 1;
          // if finished last track, move to next section if possible
          reader.set.tts({
            ...reader.tts,
            sectionIndex: newSection,
            seconds: 0,
            duration: 0,
            trackIndex: 0,
            trackDuration: []
          });
          onClick(undefined, "Download", newSection);
        } else {
          // else if last section and last track, go idle
          setAction("idle");
        }
      };
    }
  }, [reader, onClick]);

  // console.log({ action, ...reader.toJS(reader.tts) });

  return (
    <Stack
      useFlexGap
      direction="row"
      spacing={3}
      p={1}
      alignItems="center"
      overflow="hidden"
      bgcolor="var(--surface-color)"
      border={1}
      borderColor="var(--outline-variant)"
      borderRadius="var(--shape-md-round)"
    >
      <Stack useFlexGap spacing={0.5} direction="row" alignItems="center">
        <ControlButton
          name="Previous"
          disabled={reader.tts.sectionIndex === 0}
          Icon={PreviousIcon}
          onClick={() => {
            const sectionIndex = Math.max(reader.tts.sectionIndex - 1, 0);

            audioRef.current.pause();
            setAction("idle");
            reader.set.tts({
              ...reader.tts,
              sectionIndex,
              seconds: 0,
              duration: 0,
              trackIndex: 0,
              trackDuration: []
            });
            analytics.track.event("TTS Previous", {
              index: sectionIndex,
              section: reader.tts.sections[sectionIndex]
            });
          }}
        />
        <ControlButton
          primary
          name="Text to speech - Pause or Play"
          onClick={locked && action !== "Play" ? locked : onClick}
          title={
            action === "Play"
              ? "Pause"
              : locked
              ? "Login to play"
              : action === "idle"
              ? "Listen aloud"
              : undefined
          }
          disabled={action === "Download" || action === "error"}
          Icon={
            action === "Play"
              ? PauseIcon
              : locked
              ? LockedIcon
              : action === "Pause"
              ? PlayIcon
              : action === "idle"
              ? TTSIcon
              : action === "Download"
              ? DownloadingIcon
              : ErrorIcon
          }
        />
        <ControlButton
          name="Next"
          disabled={reader.tts.sectionIndex === reader.tts.maxIndex}
          Icon={ForwardIcon}
          onClick={() => {
            const sectionIndex = Math.min(
              reader.tts.sectionIndex + 1,
              reader.tts.maxIndex
            );

            audioRef.current.pause();
            setAction("idle");
            reader.set.tts({
              ...reader.tts,
              sectionIndex,
              seconds: 0,
              duration: 0,
              trackIndex: 0,
              trackDuration: []
            });
            analytics.track.event("TTS Next", {
              index: sectionIndex,
              section: reader.tts.sections[sectionIndex]
            });
          }}
        />
      </Stack>
      <Stack useFlexGap direction="row" flexGrow={1} pr={1}>
        <ListItemText
          primary={reader.tts.sections?.[reader.tts.sectionIndex]}
          primaryTypographyProps={{
            variant: "labelMd",
            color: "var(--surface-on-color)",
            sx: {
              whiteSpace: "nowrap"
            }
          }}
          secondary={
            <Stack
              useFlexGap
              direction="row"
              alignItems="center"
              spacing={1.5}
              pb={1}
            >
              <TimeLabel seconds={reader.tts.seconds} />
              <Slider
                aria-label="Time slider"
                value={reader.tts.seconds || 0}
                max={reader.tts.duration || 1}
                disabled={action === "idle"}
                onChange={(_, seconds) => {
                  if (audioRef.current.paused === false) {
                    onClick(undefined, "Pause");
                  }

                  reader.set.tts({ ...reader.tts, seconds });
                }}
                onChangeCommitted={(_, seconds) => {
                  if (audioRef.current.paused === false) {
                    audioRef.current.pause();
                  }

                  let { trackDuration, trackIndex } = reader.tts;
                  let newTrackTime = 0;
                  // console.log({ seconds, trackDuration, trackIndex });
                  for (
                    let duration = 0, i = 0, j = trackDuration.length;
                    i < j;
                    i++
                  ) {
                    duration += trackDuration[i];
                    // const runTime = trackDuration[i][2];

                    // console.log({ i, duration: trackDuration[i][2], runTime });
                    if (seconds <= duration) {
                      trackIndex = i;
                      newTrackTime = Math.abs(
                        duration - seconds - trackDuration[i]
                      );
                      // console.info("onChangeCommitted", {
                      //   duration,
                      //   thisTrack: trackDuration[i],
                      //   secondsRequested: seconds,
                      //   aboutToSet: newTrackTime
                      // });
                      break;
                    }
                  }
                  // console.log({ trackIndex, newTime });

                  setTimeout(() => {
                    // if (audioRef.current.paused) {
                    // console.info("inside here", {
                    //   seconds,
                    //   newTrackTime,
                    //   new: seconds - (trackDuration[trackIndex]?.[1] ?? 0)
                    // });
                    // console.log(1, audioRef.current.paused);
                    audioRef.current.currentTime = newTrackTime;
                    // console.log(2, audioRef.current.paused);

                    onClick(undefined, "Play");
                    // }
                  }, 100);

                  reader.set.tts({ ...reader.tts, seconds, trackIndex });
                  analytics.track.event("TTS Skip-to", {
                    index: reader.tts.sectionIndex,
                    skipTo: seconds / reader.tts.duration,
                    section: reader.tts.sections[reader.tts.sectionIndex]
                  });
                }}
                sx={{ color: "var(--secondary-color)" }}
              />
              <TimeLabel seconds={reader.tts.duration} />
            </Stack>
          }
          secondaryTypographyProps={{ component: "div" }}
        />
      </Stack>
    </Stack>
  );
}
const ControlButton = ({ name, primary, disabled, Icon, title, onClick }) => (
  <Tooltip arrow title={title}>
    <span>
      <IconButton
        aria-label={name}
        disabled={disabled}
        onClickCapture={onClick}
        sx={
          primary
            ? { bgcolor: "var(--secondary-container)" }
            : { "&:hover": { bgcolor: "var(--surface-container-high)" } }
        }
      >
        <Icon
          sx={{
            height: primary ? 20 : 16,
            width: primary ? 20 : 16,
            color: disabled
              ? "var(--surface-container-highest)"
              : primary
              ? "var(--secondary-on-container)"
              : "var(--surface-on-variant)"
          }}
        />
      </IconButton>
    </span>
  </Tooltip>
);
function TimeLabel({ seconds = 0 }) {
  // Round the seconds first
  const totalSeconds = Math.round(seconds);

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  let parts = [];
  // Add hours if present
  if (hours > 0) {
    parts.push(String(hours).padStart(2, "0"));
  }
  // Always add minutes, pad if hours are present
  parts.push(String(minutes).padStart(hours > 0 ? 2 : 1, "0"));
  // Always add seconds, pad to 2 digits
  parts.push(String(secs).padStart(2, "0"));

  return seconds === 0 ? (
    ""
  ) : (
    <Typography variant="labelSm" color="var(--surface-on-color)">
      {parts.join(":")}
      {/* {seconds.toFixed(0)} */}
    </Typography>
  );
}

function useFeatureGate(isPlaying) {
  const { analytics, user, reader, utilities } = useStore();
  const signInDialog = useDialogSignIn("Paper audio is free for the community");
  const upgradeDialog = useDialogUpgrade();
  const usageAllowed = user.premium
    ? Infinity
    : user.isAnonymous
    ? user.profile.cohort === "low"
      ? 0
      : 5
    : user.profile.cohort === "low"
    ? 5
    : 20;
  const usage = user.isAnonymous
    ? user.profile.featureGate?.audio ?? 0
    : analytics.meters.usage?.audio ?? 0;
  const isGated = usageAllowed <= usage;

  // sync between device profiles
  useEffect(() => {
    if (user.loaded && user.isAnonymous) {
      utilities.cache.get("fg-audio").then(featureUsageInCache => {
        const usersFeatureUsageCount = user.profile?.featureGate?.audio;

        if (featureUsageInCache && usersFeatureUsageCount === undefined) {
          user.data.update({ featureGate: { audio: featureUsageInCache } });

          if (usageAllowed <= featureUsageInCache) {
            analytics.track.event("Feature Gate", { audio: true });
          }
        } else if (usersFeatureUsageCount && featureUsageInCache === null) {
          utilities.cache.set("fg-audio", usersFeatureUsageCount);
        }
      });
    }
  }, [analytics, utilities, user, user.loaded, user.isAnonymous, usageAllowed]);

  // track usage for anonymous users
  useEffect(() => {
    if (isPlaying && user.loaded && user.isAnonymous && isGated === false) {
      const newUsage = usage + 1;

      user.data.update({ featureGate: { audio: newUsage } });
      utilities.cache.set("fg-audio", newUsage);

      if (newUsage <= usageAllowed) {
        analytics.track.event("Feature Gate", { audio: true });
      }
    }
  }, [
    analytics,
    utilities,
    user,
    user.loaded,
    user.isAnonymous,
    isGated,
    usageAllowed,
    isPlaying,
    usage
  ]);

  return isGated && reader.whiteLabelled === false
    ? user.isAnonymous
      ? signInDialog
      : upgradeDialog
    : undefined;
}

export default observer(TextToSpeech);
